<script lang="ts" setup>
import ViewSwitcher from '@/components/hhv3/Analyzer/ViewSwitcher.vue'
import { t } from '@/locales/i18n'
import type {
  AnalyzerReport,
  Filter,
  HandHistoryReportColumn,
  FilterCategory,
} from '@/types/api/reports'
import GwScrollCont from '@/components/lib/GwScrollCont/GwScrollCont.vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import { computed } from 'vue'
import GwMenuNew from '@/components/lib/GwMenuNew/GwMenuNew.vue'
import { getScreenUtils } from '@/components/lib/GwUtils/utils_screen'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import GwTooltipNew from '@/components/lib/GwTooltip/GwTooltipNew.vue'
import Filters, { type FilterEmits } from './Filters/Filters.vue'
import ColumnManagement, { type ColumnManagementEmits } from './ReportTable/ColumnManagement.vue'
import FilterManagement, { type FilterManagementEmits } from './Filters/FilterManagement.vue'

interface AnalyzerReportFilterBarProps {
  tstPrefix?: string
  selectedView: AnalyzerReport['selected_view']
  columns: HandHistoryReportColumn[]
  filters: Filter[]
  filterCategories: FilterCategory[]
}

const props = withDefaults(defineProps<AnalyzerReportFilterBarProps>(), {})
const { mobile } = getScreenUtils()

export type FilterBarEmits = FilterManagementEmits &
  ColumnManagementEmits &
  FilterEmits & {
    'click:view': [event: MouseEvent, view: AnalyzerReport['selected_view']]
  }

const emit = defineEmits<FilterBarEmits>()

const visibleFilters = computed(() =>
  props.filters.filter(i => i.filter.ui_value_type !== 'readonly_filename'),
)

const handleSwitchView = (e: MouseEvent, view: AnalyzerReport['selected_view']) => {
  emit('click:view', e, view)
}
</script>
<template>
  <GwBox
    variant="box_light5"
    class="d-flex w-100 mw-0 mh-0"
    style="
      border-radius: var(--app-rad-4) var(--app-rad-4) 0 0;
      padding: var(--app-pad-4);
      gap: var(--app-gap-2);
      --surface-bg: var(--app-clr-black-1);
    "
  >
    <ViewSwitcher
      :tstPrefix
      class="mw-0 mh-0 no-shrink"
      :value="selectedView"
      @click="handleSwitchView"
    />

    <GwScrollCont
      direction="horizontal"
      class="ml-auto"
      hasOverflowSurfaces
      hasResizeObserver
      hasEdgeAutoScroll
      hasScrollArrows
    >
      <Filters
        :filters
        :tstPrefix
        @update:filter="item => $emit('update:filter', item)"
        @remove:filter="item => $emit('remove:filter', item)"
      />
    </GwScrollCont>

    <GwMenuNew
      transitionVariant="dialog"
      :closeOnContentClick="false"
      boxVariant="box_dialog"
      hasClose
    >
      <template #opener="{ listeners: menuListeners, isOpen }">
        <GwTooltipNew
          :closeOnContentHover="true"
          placement="top"
          :tooltip="t('hhv3.tooltips.add_filters')"
        >
          <template #opener="{ listeners: tooltipListeners }">
            <div v-on="menuListeners">
              <GwBtn
                data-tst="open_filter_management"
                icon="mdi-filter"
                variant="btn_grey_white_icon_grey_primary-v2"
                :active="isOpen"
                v-bind="$attrs"
                v-on="tooltipListeners"
              >
                {{ mobile ? '' : t('hhv3.filters') }}

                <div class="clr-primary ml-1 weight_500">
                  {{ visibleFilters.filter(i => i.is_active).length || '' }}
                </div>
              </GwBtn>
            </div>
          </template>
        </GwTooltipNew>
      </template>
      <template #content>
        <FilterManagement
          :visibleFilters
          :filterCategories
          @update:filter="item => $emit('update:filter', item)"
          @remove:filter="item => $emit('remove:filter', item)"
        />
      </template>
    </GwMenuNew>

    <GwMenuNew
      transitionVariant="slideright"
      :closeOnContentClick="false"
      hasClose
      :disabled="selectedView !== 'TABLE'"
    >
      <template #opener="{ listeners: menuListeners, isOpen }">
        <GwTooltipNew
          :closeOnContentHover="true"
          placement="top"
          :tooltip="t('hhv3.tooltips.manage_columns')"
        >
          <template #opener="{ listeners: tooltipListeners }">
            <div v-on="menuListeners">
              <GwBtn
                data-tst="open_column_management"
                icon="mdi-table-column-plus-after"
                variant="btn_grey_white_icon_grey_primary-v2"
                :disabled="selectedView !== 'TABLE'"
                :active="isOpen"
                v-bind="$attrs"
                v-on="tooltipListeners"
              />
            </div>
          </template>
        </GwTooltipNew>
      </template>
      <template #content>
        <ColumnManagement
          :columns
          @drag:drop:column="(e, args) => $emit('drag:drop:column', e, args)"
          @toggle:column="item => $emit('toggle:column', item)"
        />
      </template>
    </GwMenuNew>
  </GwBox>
</template>
